import { useEffect } from 'react';
import { atom, useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import { ReplyType } from 'components/Reply/reply-type';
import { CHAT_BOT } from 'genesys/genesys';
import { transformer } from 'utils/chatbot-message-to-jsx/transformer';
import { SKIP_FEEDBACK_ENTRY } from 'components/Reply/PromoteInputFeedback/PromoteInputFeedback';
import { useSafeLabel } from 'hooks';
import messages from 'components/Reply/PromoteInputFeedback/messages';

export const conversationState = atom({
  key: 'conversationState',
  default: [],
});

export const useConversation = () => useRecoilValue(conversationState);

export const useSetConversation = () => useSetRecoilState(conversationState);

const getFeedbackOrNonFeedbackQ = (question, noSkipFeedback) =>
  question === SKIP_FEEDBACK_ENTRY ? noSkipFeedback : question;

export const useOutboundConversationState = () => {
  const setConversation = useSetConversation();

  const setOutboundConversation = (message, datetime) => {
    setConversation((conversation) => {
      const { isAgentFeedback } = conversation[conversation.length - 1] || {};

      return conversation.concat({
        question: message,
        id: uuidv4(),
        datetime: !!datetime ? new Date(datetime) : new Date(),
        replyType: ReplyType.Agent,
        isAgentFeedback,
      });
    });
  };

  return { setOutboundConversation };
};

export const useInboundConversationState = () => {
  const setConversation = useSetConversation();

  const setInboundConversation = (inboundResponse) => {
    const { id, message, displayName, datetime, additionalClaims } =
      inboundResponse;

    setConversation((conversation) =>
      conversation.concat({
        answer: message,
        id: id ?? uuidv4(),
        datetime: new Date(datetime),
        displayName,
        replyType: ReplyType.Agent,
        ...additionalClaims,
      }),
    );
  };

  return { setInboundConversation };
};

export const useSetConversationEffect = (queryResponse) => {
  const setConversation = useSetConversation();
  const safeLabel = useSafeLabel();

  useEffect(() => {
    const {
      answer,
      question,
      answerLinkID,
      connectors,
      dtreenodeid,
      dtreeobjectid,
      answerID,
      showicsoverlay,
    } = queryResponse ?? {};

    /*
     * When we change breadcrumb context or select category, we are not supposed
     * to update conversation history. I check the difference of the responses
     * between context change and others, notice context change response has empty
     * answerLinkID, so now we use this as a flag to decide whether to update conversation
     * history. It seems a bit tricky but efficent. If things change in the future,
     * we can also pass query type into useChatQuery and pass it out.
     */
    if (answerLinkID) {
      transformer.process(answer).then(({ result }) => {
        setConversation((conversation) =>
          conversation.concat({
            answer: result,
            defaultAnswer: answer,
            question: getFeedbackOrNonFeedbackQ(
              question,
              safeLabel(messages.skipThisAction),
            ),
            id: uuidv4(),
            datetime: new Date(),
            dtreenodeid,
            dtreeobjectid,
            answerID,
            connectors,
            by: CHAT_BOT,
            replyType:
              showicsoverlay?.toLowerCase() === 'whynothelpful' ||
              showicsoverlay?.toLowerCase() === 'whynotsavecall'
                ? ReplyType.PromoteInputFeedback
                : ReplyType.Chatbot,
          }),
        );
      });
    }
  }, [queryResponse, setConversation, safeLabel]);
};
